import type { CompanyMembership, CompanyRole } from '../companies/types';
import type { PlatformRole, User, UserProfile, UserRole } from './types';
import { DEFAULT_COMPANY_ID } from '../../shared/constants';
import { getInitials } from '../../shared/utils/user';
import { authConfigurationErrorMessage, isLocalDemoAuthEnabled, isSupabaseConfigured, requireSupabase } from './supabaseClient';

function mapPlatformRole(value: unknown): PlatformRole | null {
  return value === 'platform_owner' || value === 'platform_admin' ? value : null;
}

function mapCompanyRole(value: unknown): CompanyRole {
  if (value === 'company_admin' || value === 'admin' || value === 'manager') return 'company_admin';
  return value === 'dispatcher' || value === 'technician' ? value : 'technician';
}

function mapOperationalRole(role: CompanyRole): UserRole {
  return role;
}

function getCompanyName(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    const first = value[0];
    if (first && typeof first === 'object' && 'name' in first) {
      const name = String((first as { name?: unknown }).name ?? '').trim();
      return name || undefined;
    }
    return undefined;
  }

  if (value && typeof value === 'object' && 'name' in value) {
    const name = String((value as { name?: unknown }).name ?? '').trim();
    return name || undefined;
  }

  return undefined;
}

function getCompanyActive(value: unknown): boolean {
  if (Array.isArray(value)) value = value[0];
  if (value && typeof value === 'object' && 'is_active' in value) return Boolean((value as { is_active?: unknown }).is_active ?? true);
  return true;
}

function mapMembership(row: Record<string, unknown>): CompanyMembership | null {
  if (!getCompanyActive(row.companies)) return null;
  return {
    id: String(row.id),
    user_id: String(row.user_id),
    company_id: String(row.company_id),
    company_name: getCompanyName(row.companies),
    company_role: mapCompanyRole(row.company_role),
    is_active: Boolean(row.is_active ?? true),
    is_default: Boolean(row.is_default ?? false),
    created_at: String(row.created_at ?? new Date().toISOString()),
  };
}

function resolveOperationalMembership(memberships: CompanyMembership[]) {
  const activeMembership = memberships.find((membership) => membership.is_default) ?? memberships[0] ?? null;
  const role = activeMembership ? mapOperationalRole(activeMembership.company_role) : null;
  return { activeMembership, role };
}

function buildFallbackMembership(userId: string, platformRole: PlatformRole | null): CompanyMembership {
  return {
    id: `missing-membership-${userId}`,
    user_id: userId,
    company_id: DEFAULT_COMPANY_ID,
    company_name: platformRole ? 'Платформа' : 'Назначение не найдено',
    company_role: 'company_admin',
    is_active: false,
    is_default: true,
    created_at: new Date().toISOString(),
  };
}

function mapUser(profile: UserProfile, memberships: CompanyMembership[]): User {
  const { activeMembership, role } = resolveOperationalMembership(memberships);
  const effectiveMembership = activeMembership ?? buildFallbackMembership(profile.id, profile.platform_role);

  return {
    ...profile,
    company_id: effectiveMembership.company_id,
    company_membership_id: effectiveMembership.id,
    company_role: effectiveMembership.company_role,
    role: role ?? effectiveMembership.company_role,
    memberships,
    initials: getInitials(profile.name),
    membership_is_active: effectiveMembership.is_active,
  };
}

function mapProfile(row: Record<string, unknown>): UserProfile {
  return {
    id: String(row.id),
    email: String(row.email),
    name: String(row.name),
    phone: row.phone ? String(row.phone) : null,
    platform_role: mapPlatformRole(row.platform_role),
    is_active: Boolean(row.is_active ?? true),
    created_at: String(row.created_at ?? new Date().toISOString()),
  };
}

function buildLocalMembership(role: UserRole): CompanyMembership {
  return {
    id: `local-membership-${role}`,
    user_id: `local-${role}`,
    company_id: DEFAULT_COMPANY_ID,
    company_name: 'Кворум Demo',
    company_role: role,
    is_active: true,
    is_default: true,
    created_at: new Date().toISOString(),
  };
}

function buildLocalUser(email: string, role: UserRole): User {
  const membership = buildLocalMembership(role);
  const localNameByRole: Record<UserRole, string> = {
    company_admin: 'Локальный администратор компании',
    dispatcher: 'Локальный диспетчер',
    technician: 'Локальный техник',
  };

  return mapUser(
    {
      id: membership.user_id,
      email,
      name: localNameByRole[role],
      phone: null,
      platform_role: null,
      is_active: true,
      created_at: new Date().toISOString(),
    },
    [membership],
  );
}

function buildLocalSuperAdminUser(email: string): User {
  const membership = buildLocalMembership('company_admin');
  return mapUser(
    {
      id: 'local-superadmin',
      email,
      name: 'Локальный суперадмин',
      phone: null,
      platform_role: 'platform_owner',
      is_active: true,
      created_at: new Date().toISOString(),
    },
    [membership],
  );
}

export async function loginWithPassword(email: string, password: string): Promise<User> {
  if (!isSupabaseConfigured) {
    if (!isLocalDemoAuthEnabled) {
      localStorage.removeItem('fieldsnap_local_user');
      throw new Error(authConfigurationErrorMessage);
    }

    const lc = email.toLowerCase();
    if (lc.includes('super')) return buildLocalSuperAdminUser(email);
    const role: UserRole = lc.includes('admin') || lc.includes('manager')
      ? 'company_admin'
      : lc.includes('disp')
      ? 'dispatcher'
      : 'technician';
    return buildLocalUser(email, role);
  }

  const { data, error } = await requireSupabase().auth.signInWithPassword({ email, password });
  if (error) throw error;
  if (!data.user) throw new Error('Пользователь не найден');
  const profile = await fetchUserProfile(data.user.id);
  if (!profile) throw new Error('Профиль пользователя не найден');
  if (!profile.platform_role && (!profile.memberships || profile.memberships.length === 0)) throw new Error('Компания деактивирована. Вход временно недоступен.');
  return profile;
}

export async function fetchUserProfile(userId: string): Promise<User | null> {
  if (!isSupabaseConfigured) return null;

  const [{ data: profileData, error: profileError }, { data: membershipData, error: membershipError }] =
    await Promise.all([
      requireSupabase().from('user_profiles').select('*').eq('id', userId).maybeSingle(),
      requireSupabase().from('company_memberships')
        .select('id, user_id, company_id, company_role, is_active, is_default, created_at, companies(name,is_active)')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: true }),
    ]);

  if (profileError) throw profileError;
  if (membershipError) throw membershipError;
  if (!profileData) return null;

  const profile = mapProfile(profileData);
  const memberships = (membershipData ?? []).map((row) => mapMembership(row as Record<string, unknown>)).filter(Boolean) as CompanyMembership[];
  if (!profile.is_active) return mapUser(profile, []);
  return mapUser(profile, memberships);
}

export async function getCurrentSessionUser(): Promise<User | null> {
  if (!isSupabaseConfigured) {
    if (!isLocalDemoAuthEnabled) {
      localStorage.removeItem('fieldsnap_local_user');
      return null;
    }

    const raw = localStorage.getItem('fieldsnap_local_user');
    if (!raw) return null;

    try {
      return JSON.parse(raw) as User;
    } catch {
      localStorage.removeItem('fieldsnap_local_user');
      return null;
    }
  }

  const { data, error } = await requireSupabase().auth.getSession();
  if (error) throw error;
  const authUser = data.session?.user;
  if (!authUser) return null;
  return await fetchUserProfile(authUser.id);
}

export async function logoutUser() {
  if (!isSupabaseConfigured) {
    localStorage.removeItem('fieldsnap_local_user');
    return;
  }
  const { error } = await requireSupabase().auth.signOut();
  if (error) throw error;
}

// 1.30.0 / P0-4 — Password recovery flow.
//
// Состоит из двух независимых шагов:
//
//   1) requestPasswordReset(email)   — пользователь на /forgot-password
//                                       вводит email, Supabase шлёт письмо
//                                       со ссылкой вида
//                                       `<redirectTo>#access_token=...&type=recovery`.
//   2) completePasswordRecovery(pwd) — пользователь, попавший по этой
//                                       ссылке на /reset-password, ввёл
//                                       новый пароль. К этому моменту
//                                       Supabase уже создал временную
//                                       recovery-сессию (через парсинг
//                                       hash-параметров в SDK) и событие
//                                       `PASSWORD_RECOVERY` пришло в
//                                       `onAuthStateChange`. Здесь мы
//                                       просто завершаем смену пароля
//                                       через `auth.updateUser`.
//
// Безопасность раскрытия email: `resetPasswordForEmail` от Supabase
// НЕ возвращает 404 для несуществующего email — это защита от user
// enumeration. UI на этой основе всегда показывает нейтральный success
// (см. ForgotPassword.tsx).

/**
 * `redirectTo` для письма со ссылкой на сброс пароля.
 * Считаем, что фронт работает на том же origin, что и запрос:
 * - Локально:   http://localhost:5173/reset-password
 * - Vercel:     https://<preview>.vercel.app/reset-password
 * - Кастомный домен (Б4 в ROADMAP): https://app.fieldsnap.ru/reset-password
 *
 * Этот origin ОБЯЗАН быть прописан в Supabase Dashboard → Authentication →
 * URL Configuration → Redirect URLs, иначе Supabase отклонит письмо.
 */
function buildPasswordResetRedirectTo(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return `${window.location.origin}/reset-password`;
}

export async function requestPasswordReset(email: string): Promise<void> {
  if (!isSupabaseConfigured) {
    // В demo-режиме reset не имеет смысла — все «пользователи» эфемерны.
    // Бросаем понятную ошибку вместо тихого no-op, чтобы разработчик/тестер
    // не думал, что письмо ушло.
    throw new Error(authConfigurationErrorMessage);
  }

  const trimmed = email.trim();
  if (!trimmed) throw new Error('Введите email');

  const { error } = await requireSupabase().auth.resetPasswordForEmail(trimmed, {
    redirectTo: buildPasswordResetRedirectTo(),
  });
  if (error) throw error;
}

export async function completePasswordRecovery(newPassword: string): Promise<void> {
  if (!isSupabaseConfigured) {
    throw new Error(authConfigurationErrorMessage);
  }

  if (newPassword.length < 8) {
    throw new Error('Пароль должен быть не короче 8 символов');
  }

  // Внутри ждём, что recovery-сессия уже открыта Supabase SDK через
  // обработку hash-параметров в URL (это происходит автоматически при
  // загрузке страницы с `#access_token=...&type=recovery`). Если сессии
  // нет — `updateUser` вернёт `AuthSessionMissingError`.
  const { error } = await requireSupabase().auth.updateUser({ password: newPassword });
  if (error) throw error;
}
